<template>
    <VJoyHighlight class="overall-rating" level="neutral">
        <VJoyWrapper direction="column" :item-stretch="false" :no-margin="true" justify="flex-start" align="flex-start">
            <VJoyWrapper class="overall-rating__header" direction="column" justify="center" item-stretch :no-margin="true" row-gap="1">
                <VJoyText preset="secondary-600" tag-name="h2">{{ formatter.format(ratings.general) }}</VJoyText>
                <VJoyRatingStars :rating-value="ratings.general" size="large" />
                <VJoyText preset="primary-200" tag-name="p">
                    {{ t('profile.resume.appraisals.rating.out.of', {count: appraisalCounter}) }}
                </VJoyText>
            </VJoyWrapper>
            <VJoyWrapper
                :style="{display: collapsed ? 'none' : 'flex'}"
                class="overall-rating__details"
                direction="column"
                justify="flex-start"
                :no-margin="true"
                row-gap="4"
            >
                <RatingDetailItem
                    :title="t('profile.resume.appraisals.label.quality')"
                    :rating="ratings.quality"
                    :icon="'check-circle' as JoyIconsId"
                />
                <RatingDetailItem :title="t('profile.resume.appraisals.label.deadline')" :rating="ratings.delay" :icon="'clock' as JoyIconsId" />
                <RatingDetailItem
                    :title="t('profile.resume.appraisals.label.communication')"
                    :rating="ratings.communication"
                    :icon="'message' as JoyIconsId"
                />
            </VJoyWrapper>
            <ShowMore
                class="overall-rating__toggle"
                :collapsed="collapsed"
                :label="collapsed ? t('profile.resume.appraisals.show.details') : t('profile.resume.appraisals.hide.details')"
                test-id="rating-details-toggle"
                size="small"
                @click="toggleCollapsed"
            />
        </VJoyWrapper>
    </VJoyHighlight>
</template>

<script setup lang="ts">
    import {ref} from 'vue';
    import {useTranslation} from '#imports';
    import {VJoyHighlight, VJoyRatingStars, VJoyText, VJoyWrapper} from '@maltjoy/core-vue';
    import type {JoyIconsId} from '@maltjoy/icons';
    import ShowMore from '~/components/_common/show-more/ShowMore.vue';
    import RatingDetailItem from '~/features/appraisals/ui/RatingDetailItem.vue';
    import type {AppraisalAverageRatingResource} from '~/types/api-raw-types';
    import {useLocale} from '#build/imports';

    const {locale} = useLocale();
    const {t} = useTranslation();

    type Props = {
        ratings: AppraisalAverageRatingResource;
        appraisalCounter: number;
    };
    defineProps<Props>();

    const collapsed = ref(true);

    function toggleCollapsed() {
        collapsed.value = !collapsed.value;
    }

    const formatter = new Intl.NumberFormat(locale.value, {minimumFractionDigits: 1, maximumFractionDigits: 1});
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint' as joy;

    .overall-rating {
        background-color: var(--joy-color-neutral-10);
        border: 1px solid var(--joy-color-neutral-30);
        width: 100%;
        max-width: 100%;
        flex-shrink: 0;

        @include joy.screen_xs(min) {
            max-width: 180px;
        }

        &__header {
            border-bottom: 1px solid var(--joy-color-neutral-30);
            padding-bottom: var(--joy-core-spacing-5);
            padding-top: var(--joy-core-spacing-2);
            width: 100%;
        }

        &__details {
            width: 100%;
            align-items: center;

            @include joy.screen_xs(min) {
                display: flex !important;
                align-items: flex-start;
            }
        }

        &__toggle {
            display: block;
            width: 100%;

            :deep(.joy-button) {
                width: 100%;
            }

            @include joy.screen_xs(min) {
                display: none;
            }
        }
    }
</style>
